import type { Attachment, UploadTicket } from "@khazzin/api-client";
import { useCallback, useRef, useState } from "react";

/**
 * Uploading a file, in three steps and one hook.
 *
 * Reserve, post, confirm. The bytes go straight to object storage rather than
 * through our service — a form signed for exactly one object, one type and one
 * size range — so what this does is bookend that POST: ask for the form, send
 * the file to wherever the form says, then tell the server it landed so the
 * bytes can be checked against what they claimed to be.
 *
 * **XMLHttpRequest, not fetch.** Only one of them reports upload progress, and
 * a ten-megabyte spreadsheet on a warehouse connection is thirty seconds of a
 * screen that has to say something. Everything else here is fetch.
 */

/** What this hook needs from the API client, and no more. */
export interface UploadApi {
	beginUpload(request: {
		purpose: string;
		ownerId: string;
		filename: string;
		contentType: string;
		sizeBytes: number;
	}): Promise<UploadTicket>;
	completeUpload(id: string): Promise<Attachment>;
}

export type UploadState =
	| { status: "idle" }
	| { status: "uploading"; progress: number; filename: string }
	| { status: "done"; attachment: Attachment }
	| { status: "error"; error: Error };

export interface UseUploadOptions {
	api: UploadApi;
	purpose: string;
	ownerId: string;
}

export interface Upload {
	state: UploadState;
	/** Uploads a file and resolves with the ready attachment. */
	upload: (file: File) => Promise<Attachment>;
	/** Abandons an upload in flight, and forgets the last result. */
	reset: () => void;
}

export function useUpload({ api, purpose, ownerId }: UseUploadOptions): Upload {
	const [state, setState] = useState<UploadState>({ status: "idle" });
	const inFlight = useRef<XMLHttpRequest | null>(null);

	const reset = useCallback(() => {
		inFlight.current?.abort();
		inFlight.current = null;
		setState({ status: "idle" });
	}, []);

	const upload = useCallback(
		async (file: File): Promise<Attachment> => {
			setState({ status: "uploading", progress: 0, filename: file.name });
			try {
				const ticket = await api.beginUpload({
					purpose,
					ownerId,
					filename: file.name,
					// A file picked from a Windows machine often arrives with an
					// empty type, so the extension is the fallback. The server
					// sniffs the bytes on completion either way, which is the
					// check that actually decides.
					contentType: file.type === "" ? guessType(file.name) : file.type,
					sizeBytes: file.size,
				});

				await post(
					ticket,
					file,
					(progress) => {
						setState({ status: "uploading", progress, filename: file.name });
					},
					inFlight,
				);

				const attachment = await api.completeUpload(ticket.attachment.id);
				setState({ status: "done", attachment });
				return attachment;
			} catch (cause) {
				const error = cause instanceof Error ? cause : new Error(String(cause));
				setState({ status: "error", error });
				throw error;
			} finally {
				inFlight.current = null;
			}
		},
		[api, ownerId, purpose],
	);

	return { state, upload, reset };
}

/** Posts the file to the signed form, reporting progress as it goes. */
function post(
	ticket: UploadTicket,
	file: File,
	onProgress: (progress: number) => void,
	inFlight: { current: XMLHttpRequest | null },
): Promise<void> {
	return new Promise((resolve, reject) => {
		const form = new FormData();
		// The signed fields first and the file last. Cloud Storage reads the
		// policy from the fields it has seen by the time the file part starts,
		// so a file appended before them is rejected with a message about a
		// missing policy — which is a confusing way to learn about field order.
		for (const [name, value] of Object.entries(ticket.upload.fields)) {
			form.append(name, value);
		}
		form.append("file", file);

		const xhr = new XMLHttpRequest();
		inFlight.current = xhr;
		xhr.open("POST", ticket.upload.url);
		xhr.upload.addEventListener("progress", (event) => {
			if (event.lengthComputable && event.total > 0) {
				onProgress(Math.round((event.loaded / event.total) * 100));
			}
		});
		xhr.addEventListener("load", () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve();
				return;
			}
			reject(new Error(`upload failed with ${xhr.status}`));
		});
		xhr.addEventListener("error", () => reject(new Error("upload failed")));
		xhr.addEventListener("abort", () => reject(new Error("upload cancelled")));
		xhr.send(form);
	});
}

/** The two types an import accepts, by extension. */
function guessType(filename: string): string {
	return filename.toLowerCase().endsWith(".xlsx")
		? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
		: "text/csv";
}
